import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { API_URL } from '@core/config/api-url.token';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { Observable } from 'rxjs';
import { EstimateDto, PagedEstimatesDto } from '../../dtos/estimate.dto';
import { EstimateItemDto } from '../../dtos/estimate-item.dto';
import { InvoiceDto, PagedInvoicesDto } from '../../dtos/invoice.dto';
import { PagedInvoiceRowsDto } from '../../dtos/invoice-row.dto';
import { PagedClientSearchResultsDto, PagedPatientSearchResultsDto } from '../../dtos/party-search.dto';
import { PagedPaymentsDto } from '../../dtos/payment.dto';

// A generous fixed page size for the "fetch every row/payment of one invoice" calls -- both
// `GET /api/billing/invoices/{id}/rows` and `GET /api/billing/payments` are paged endpoints
// (page/pageSize default to 1/100), but the invoice detail page needs every row/payment for a
// single invoice at once, mirroring `EstimatesListParams`'s own "unpaged in practice" precedent
// for `getEstimateItems`.
const DETAIL_LIST_PAGE_SIZE = 200;

// The client/patient search dropdowns only ever show a handful of results at a time -- mirrors
// `patient-tutor-search.util.ts`'s own bounded page size for the exact same "typeahead" use case.
const SEARCH_PAGE_SIZE = 20;

export interface InvoicesListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly departmentId?: number;
  readonly onlyPending?: boolean;
  readonly paymentState?: 'pending' | 'paid' | 'overdue' | null;
  readonly dateFrom?: string | null;
  readonly dateTo?: string | null;
  readonly clientIds?: readonly string[] | null;
  readonly invoiceDueDateFrom?: string | null;
  readonly invoiceDueDateTo?: string | null;
  readonly datePaidFrom?: string | null;
  readonly datePaidTo?: string | null;
  readonly dueSumMin?: number | null;
  readonly dueSumMax?: number | null;
  readonly invoiceNumber?: string | null;
  readonly invoicePrefixContains?: string | null;
  readonly sortBy?: string;
  readonly sortDir?: 'asc' | 'desc';
}

export interface EstimatesListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly clientIds?: readonly string[] | null;
  readonly patientIds?: readonly string[] | null;
  readonly statuses?: readonly number[] | null;
  readonly archived?: boolean | null;
  readonly dateFrom?: string | null;
  readonly dateTo?: string | null;
  readonly sortBy?: string;
  readonly sortDir?: 'asc' | 'desc';
}

@Injectable()
export class BillingRemoteDatasource {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  public listInvoices(params: InvoicesListParams): Observable<PagedInvoicesDto> {
    const httpParams = this.buildInvoiceFilterParams(params, { page: params.page, pageSize: params.pageSize });
    return this.http.get<PagedInvoicesDto>(`${this.apiUrl}${API_ENDPOINTS.billing.invoices}`, { params: httpParams });
  }

  public getInvoiceById(invoiceId: string): Observable<InvoiceDto> {
    return this.http.get<InvoiceDto>(`${this.apiUrl}${API_ENDPOINTS.billing.invoiceById(invoiceId)}`);
  }

  public getInvoiceRows(invoiceId: string): Observable<PagedInvoiceRowsDto> {
    return this.http.get<PagedInvoiceRowsDto>(`${this.apiUrl}${API_ENDPOINTS.billing.invoiceRows(invoiceId)}`, {
      params: { pageSize: DETAIL_LIST_PAGE_SIZE },
    });
  }

  public getInvoicePayments(invoiceId: string): Observable<PagedPaymentsDto> {
    return this.http.get<PagedPaymentsDto>(`${this.apiUrl}${API_ENDPOINTS.billing.payments}`, {
      params: { invoiceId, pageSize: DETAIL_LIST_PAGE_SIZE },
    });
  }

  public getInvoicePdf(invoiceId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}${API_ENDPOINTS.billing.invoicePdf(invoiceId)}`, { responseType: 'blob' });
  }

  public listEstimates(params: EstimatesListParams): Observable<PagedEstimatesDto> {
    let httpParams: Record<string, string | number | boolean> = { page: params.page, pageSize: params.pageSize };
    if (params.sortBy) httpParams = { ...httpParams, sortBy: params.sortBy, sortDir: params.sortDir ?? 'asc' };
    if (params.clientIds?.length) httpParams = { ...httpParams, clientIds: params.clientIds.join(',') };
    if (params.patientIds?.length) httpParams = { ...httpParams, patientIds: params.patientIds.join(',') };
    if (params.statuses?.length) httpParams = { ...httpParams, statuses: params.statuses.join(',') };
    if (params.archived !== null && params.archived !== undefined) {
      httpParams = { ...httpParams, archived: params.archived };
    }
    if (params.dateFrom) httpParams = { ...httpParams, dateFrom: params.dateFrom };
    if (params.dateTo) httpParams = { ...httpParams, dateTo: params.dateTo };

    return this.http.get<PagedEstimatesDto>(`${this.apiUrl}${API_ENDPOINTS.billing.estimates}`, { params: httpParams });
  }

  public getEstimateById(estimateId: string): Observable<EstimateDto> {
    return this.http.get<EstimateDto>(`${this.apiUrl}${API_ENDPOINTS.billing.estimateById(estimateId)}`);
  }

  public getEstimateItems(estimateId: string): Observable<EstimateItemDto[]> {
    return this.http.get<EstimateItemDto[]>(`${this.apiUrl}${API_ENDPOINTS.billing.estimateItems(estimateId)}`);
  }

  public getEstimatePdf(estimateId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}${API_ENDPOINTS.billing.estimatePdf(estimateId)}`, { responseType: 'blob' });
  }

  // `GET /api/clients/search` is `ClientsController`'s own endpoint (billing has no client
  // resource of its own) -- an empty `term` intentionally still resolves (see the controller's own
  // doc comment), so the drawer can list clients before the user types anything, same as
  // `createPatientTutorSearch`'s "always searches" precedent.
  public searchClients(term: string): Observable<PagedClientSearchResultsDto> {
    return this.http.get<PagedClientSearchResultsDto>(`${this.apiUrl}${API_ENDPOINTS.clients.search}`, {
      params: { term, page: 1, pageSize: SEARCH_PAGE_SIZE },
    });
  }

  // `GET /api/patients/search` mirrors `searchClients` above (`PatientsController`'s own endpoint).
  public searchPatients(term: string): Observable<PagedPatientSearchResultsDto> {
    return this.http.get<PagedPatientSearchResultsDto>(`${this.apiUrl}${API_ENDPOINTS.patients.search}`, {
      params: { term, page: 1, pageSize: SEARCH_PAGE_SIZE },
    });
  }

  private buildInvoiceFilterParams(
    params: Omit<InvoicesListParams, 'page' | 'pageSize' | 'sortBy' | 'sortDir'>,
    extra: Record<string, string | number>,
  ): Record<string, string | number | boolean> {
    let httpParams: Record<string, string | number | boolean> = { ...extra };
    if (params.departmentId !== undefined) httpParams = { ...httpParams, departmentId: params.departmentId };
    if (params.onlyPending) httpParams = { ...httpParams, onlyPending: true };
    if (params.paymentState) httpParams = { ...httpParams, paymentState: params.paymentState };
    if (params.dateFrom) httpParams = { ...httpParams, dateFrom: params.dateFrom };
    if (params.dateTo) httpParams = { ...httpParams, dateTo: params.dateTo };
    if (params.clientIds?.length) httpParams = { ...httpParams, clientIds: params.clientIds.join(',') };
    if (params.invoiceDueDateFrom) httpParams = { ...httpParams, invoiceDueDateFrom: params.invoiceDueDateFrom };
    if (params.invoiceDueDateTo) httpParams = { ...httpParams, invoiceDueDateTo: params.invoiceDueDateTo };
    if (params.datePaidFrom) httpParams = { ...httpParams, datePaidFrom: params.datePaidFrom };
    if (params.datePaidTo) httpParams = { ...httpParams, datePaidTo: params.datePaidTo };
    if (params.dueSumMin !== null && params.dueSumMin !== undefined) {
      httpParams = { ...httpParams, dueSumMin: params.dueSumMin };
    }
    if (params.dueSumMax !== null && params.dueSumMax !== undefined) {
      httpParams = { ...httpParams, dueSumMax: params.dueSumMax };
    }
    if (params.invoiceNumber) httpParams = { ...httpParams, invoiceNumber: params.invoiceNumber };
    if (params.invoicePrefixContains) httpParams = { ...httpParams, invoicePrefixContains: params.invoicePrefixContains };

    return httpParams;
  }
}
